import React, { useState, useMemo, useEffect } from 'react';
import { Search, Filter, TrendingUp, BarChart3 } from 'lucide-react';
import ArchiveTable from '../components/ArchiveTable';
import { PerformanceChart, ShotMap } from '../components/analytics/Visuals';
import { getStats, groupByMonth, monthlyWinRateSeries, outcome } from '../data/archiveData';
import { fetchArchive } from '../api';
import Seo from '../components/Seo';

export default function SinglesArchive() {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [archive, setArchive] = useState([]);

  useEffect(() => {
    (async () => {
      try { setArchive(await fetchArchive()); } catch (e) { /* empty */ }
    })();
  }, []);

  const filtered = useMemo(() => archive.filter((r) => {
    const q = r.match.toLowerCase().includes(query.toLowerCase());
    const o = outcome(r.result);
    const f = filter === 'all' || (filter === 'won' && o === 'won') || (filter === 'lost' && o === 'lost');
    return q && f;
  }), [query, filter, archive]);

  const stats = getStats(archive);
  const groups = useMemo(() => groupByMonth(filtered), [filtered]);
  const series = useMemo(() => monthlyWinRateSeries(archive), [archive]);

  return (
    <div>
      <Seo
        title="Full Tips Archive | Transparent Results & Win Rate – 24BetBox"
        description="Every premium single football tip we have released, published openly with wins and losses. Full transparency with an automatically calculated win rate."
        path="/singles-archive"
      />
      <section className="hero-gradient border-b border-[#2e2822]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
          <p className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-orange-500">24betbox.com</p>
          <h1 className="mt-2 font-heading text-5xl font-extrabold text-white sm:text-6xl">Full Archive</h1>
          <p className="mt-4 max-w-2xl text-neutral-300">Every premium single tip we have ever released, published openly with wins and losses. Full transparency, automatically calculated.</p>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="rounded-xl border border-[#2e2822] bg-[#1b1712] p-4"><p className="font-heading text-3xl font-extrabold text-white">{stats.played}</p><p className="text-xs text-neutral-500">Tips Settled</p></div>
            <div className="rounded-xl border border-[#2e2822] bg-[#1b1712] p-4"><p className="font-heading text-3xl font-extrabold text-emerald-400">{stats.wins}</p><p className="text-xs text-neutral-500">Wins</p></div>
            <div className="rounded-xl border border-[#2e2822] bg-[#1b1712] p-4"><p className="font-heading text-3xl font-extrabold text-red-400">{stats.losses}</p><p className="text-xs text-neutral-500">Losses</p></div>
            <div className="rounded-xl border border-orange-500/40 bg-orange-500/10 p-4"><p className="font-heading text-3xl font-extrabold orange-text-gradient">{stats.winRate}%</p><p className="text-xs text-neutral-400">Win Rate</p></div>
          </div>
        </div>
      </section>

      {/* Analytics row */}
      <section className="bg-[#15120f] py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="card-dark rounded-2xl p-6 lg:col-span-2">
              <p className="flex items-center gap-2 font-heading text-lg font-bold text-white"><TrendingUp className="h-5 w-5 text-orange-500" /> Monthly Win Rate Trend</p>
              <div className="mt-4"><PerformanceChart data={series} /></div>
            </div>
            <div className="card-dark rounded-2xl p-6">
              <p className="flex items-center gap-2 font-heading text-lg font-bold text-white"><BarChart3 className="h-5 w-5 text-orange-500" /> Shot Map Model</p>
              <div className="mx-auto mt-4 h-[260px] w-full max-w-[320px]"><ShotMap /></div>
              <div className="mt-3 flex justify-center gap-4 text-xs text-neutral-400"><span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-orange-500" /> Goal</span><span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full border border-sky-400" /> Attempt</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters + table grouped by month */}
      <section className="bg-[#15120f] pb-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search match or team..." className="w-full rounded-lg border border-[#2e2822] bg-[#1b1712] py-2.5 pl-10 pr-4 text-sm text-white placeholder-neutral-500 outline-none focus:border-orange-500/60" />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-neutral-500" />
              {[['all', 'All'], ['won', 'Won'], ['lost', 'Lost']].map(([key, label]) => (
                <button key={key} onClick={() => setFilter(key)} className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${filter === key ? 'btn-orange' : 'border border-[#2e2822] bg-[#1b1712] text-neutral-300 hover:border-orange-500/50'}`}>{label}</button>
              ))}
            </div>
          </div>

          {groups.length === 0 ? (
            <div className="rounded-xl border border-[#2e2822] bg-[#1b1712] p-12 text-center text-neutral-400">No results match your search.</div>
          ) : (
            <div className="space-y-10">
              {groups.map((g) => (
                <div key={g.key}>
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    <h3 className="font-heading text-2xl font-extrabold text-white">{g.key}</h3>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-emerald-400">{g.stats.wins}W</span>
                      <span className="text-red-400">{g.stats.losses}L</span>
                      <span className="rounded-full bg-orange-500/15 px-3 py-1 font-bold text-orange-400">{g.stats.winRate}% Win</span>
                    </div>
                  </div>
                  <ArchiveTable rows={g.rows} />
                </div>
              ))}
            </div>
          )}
          <p className="mt-6 text-xs text-neutral-500">Note: Archived results are provided for informational purposes. Past performance does not guarantee future results.</p>
        </div>
      </section>
    </div>
  );
}
